/**
 * VillaMax — Cart Banner
 *
 * Puebla #vx-cart-banner con un mensaje contextual segun:
 *   - Ubicacion guardada del cliente (localStorage key: farmalkosto_location)
 *   - Subtotal actual del carrito (fetch /cart.js)
 *
 * Depende de location-system.js (para emitir 'farmalkosto:location-changed'
 * y proveer window.FarmalkostoLocation.openModal).
 *
 * No usa template literals por compatibilidad con el editor de Shopify.
 */
(function () {
  "use strict";

  var BANNER_ID = "vx-cart-banner";
  var STORAGE_KEY = "farmalkosto_location";
  var STORAGE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var FREE_SHIPPING_THRESHOLD = 20000;

  var TARIFFS = {
    1: 0,       // Tier 1: Bogota D.C. + radio local drugstore (gratis >= 20.000)
    2: 10000,   // Tier 2: Bogota D.C. fuera de radio
    3: 5000,    // Tier 3: Cundinamarca + radio Madrid
    4: 13400    // Tier 4: resto del pais
  };

  // Pedido minimo por tier (bloquea checkout via Cart Validation Function)
  var MIN_ORDER = {
    1: 20000,   // alineado con umbral de envio gratis
    2: 30000,
    3: 30000,
    4: 30000
  };

  function moneyCOP(n) {
    return "$" + Math.round(n).toLocaleString("es-CO");
  }

  function readLocation() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      // TTL: mismo criterio que location-system.js (saved_at + 30 dias)
      if (parsed.saved_at && Date.now() - parsed.saved_at > STORAGE_TTL_MS) {
        return null;
      }
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function fetchCart() {
    return fetch("/cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function buildMessage(location, cart) {
    var subtotal = 0;
    if (cart && typeof cart.items_subtotal_price === "number") {
      subtotal = cart.items_subtotal_price / 100;
    }

    if (!location) {
      // Sin ubicacion: el server-side no valida (no hay tier en cart attrs),
      // pero no bloqueamos el boton para no confundir; el banner ya pide ubicacion.
      return {
        type: "empty",
        icon: "\uD83D\uDCCD",
        title: "Configura tu direccion",
        sub: "Conoce tu costo de envio y si calificas a envio gratis",
        ctaLabel: "Agregar ubicacion",
        blockCheckout: false
      };
    }

    var tier = Number(location.tier) || 4;
    var address = location.address || "Tu ubicacion";
    var rate = TARIFFS[tier];

    if (tier === 1) {
      var missing = FREE_SHIPPING_THRESHOLD - subtotal;
      if (missing <= 0) {
        return {
          type: "free",
          icon: "\uD83C\uDF89",
          title: "\u00A1Tu pedido califica para envio GRATIS!",
          sub: "Entrega local sin costo adicional",
          address: address,
          progress: 100,
          blockCheckout: false
        };
      }
      // Tier 1: MIN_ORDER[1] === FREE_SHIPPING_THRESHOLD, por eso bloquea tambien
      return {
        type: "progress",
        icon: "\uD83D\uDE9A",
        title: "Te faltan " + moneyCOP(missing) + " para envio GRATIS",
        sub: "Entrega local \u2014 minimo " + moneyCOP(FREE_SHIPPING_THRESHOLD),
        address: address,
        progress: Math.max(0, Math.min(100, (subtotal / FREE_SHIPPING_THRESHOLD) * 100)),
        blockCheckout: true,
        blockReason: "Agrega " + moneyCOP(missing) + " mas para continuar al pago (minimo " + moneyCOP(FREE_SHIPPING_THRESHOLD) + ")"
      };
    }

    // Tier 2/3/4: tarifa fija con pedido minimo $30.000
    var minOrder = MIN_ORDER[tier];
    var belowMin = subtotal < minOrder;
    var missingMin = Math.max(0, minOrder - subtotal);
    var progressMin = Math.max(0, Math.min(100, (subtotal / minOrder) * 100));

    if (tier === 2) {
      if (belowMin) {
        return {
          type: "below-min",
          icon: "\uD83D\uDE9A",
          title: "Te faltan " + moneyCOP(missingMin) + " para el pedido minimo",
          sub: "Envio a Bogota: " + moneyCOP(rate) + " \u00B7 Minimo " + moneyCOP(minOrder),
          address: address,
          progress: progressMin,
          blockCheckout: true,
          blockReason: "Agrega " + moneyCOP(missingMin) + " mas para continuar al pago (minimo " + moneyCOP(minOrder) + ")"
        };
      }
      return {
        type: "paid",
        icon: "\uD83D\uDE9A",
        title: "Envio a Bogota: " + moneyCOP(rate),
        sub: "Fuera de radio local",
        address: address,
        blockCheckout: false
      };
    }

    if (tier === 3) {
      if (belowMin) {
        return {
          type: "below-min",
          icon: "\uD83D\uDE9A",
          title: "Te faltan " + moneyCOP(missingMin) + " para el pedido minimo",
          sub: "Envio local Madrid: " + moneyCOP(rate) + " \u00B7 Minimo " + moneyCOP(minOrder),
          address: address,
          progress: progressMin,
          blockCheckout: true,
          blockReason: "Agrega " + moneyCOP(missingMin) + " mas para continuar al pago (minimo " + moneyCOP(minOrder) + ")"
        };
      }
      return {
        type: "paid",
        icon: "\uD83D\uDE9A",
        title: "Envio local Madrid: " + moneyCOP(rate),
        sub: "Radio drogueria Madrid",
        address: address,
        blockCheckout: false
      };
    }

    // Tier 4 (nacional)
    if (belowMin) {
      return {
        type: "below-min",
        icon: "\uD83D\uDCE6",
        title: "Te faltan " + moneyCOP(missingMin) + " para el pedido minimo",
        sub: "Envio nacional: " + moneyCOP(rate) + " \u00B7 Minimo " + moneyCOP(minOrder),
        address: address,
        progress: progressMin,
        blockCheckout: true,
        blockReason: "Agrega " + moneyCOP(missingMin) + " mas para continuar al pago (minimo " + moneyCOP(minOrder) + ")"
      };
    }
    return {
      type: "paid",
      icon: "\uD83D\uDCE6",
      title: "Envio nacional: " + moneyCOP(rate),
      sub: "Fuera de zonas locales",
      address: address,
      blockCheckout: false
    };
  }

  function escapeHtml(str) {
    if (str == null) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function render(msg) {
    var el = document.getElementById(BANNER_ID);
    if (!el) return;

    var html = "";
    html += '<div class="vx-cart-banner__row">';
    html += '  <span class="vx-cart-banner__icon" aria-hidden="true">' + msg.icon + "</span>";
    html += '  <div class="vx-cart-banner__content">';
    html += '    <div class="vx-cart-banner__title">' + escapeHtml(msg.title) + "</div>";

    if (msg.sub) {
      html += '    <div class="vx-cart-banner__sub">' + escapeHtml(msg.sub) + "</div>";
    }
    if (msg.address) {
      html += '    <div class="vx-cart-banner__address">\uD83D\uDCCD ' + escapeHtml(msg.address) + "</div>";
    }

    html += "  </div>";

    // Usamos data-fl-loc-open para delegar a location-system.js (que ya tiene handler)
    if (msg.type === "empty") {
      html +=
        '  <button type="button" class="vx-cart-banner__cta" data-fl-loc-open>' +
        escapeHtml(msg.ctaLabel) +
        "</button>";
    } else {
      var pencil =
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" ' +
        'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M12 20h9"/>' +
        '<path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>' +
        "</svg>";
      html +=
        '  <button type="button" class="vx-cart-banner__change" data-fl-loc-open ' +
        'aria-label="Cambiar ubicacion" title="Cambiar ubicacion">' +
        pencil +
        "</button>";
    }

    html += "</div>";

    if (typeof msg.progress === "number") {
      html +=
        '<div class="vx-cart-banner__progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' +
        Math.round(msg.progress) + '">';
      html += '  <div class="vx-cart-banner__progress-fill" style="width:' + msg.progress + '%"></div>';
      html += "</div>";
    }

    el.innerHTML = html;
    el.setAttribute("data-type", msg.type);
  }

  // Selectores candidatos para el boton de checkout (varian segun tema/Shopify)
  var CHECKOUT_SELECTORS = [
    'button[name="checkout"]',
    '#CartDrawer-Checkout',
    '#checkout',
    '.cart__checkout-button',
    '[data-testid="Checkout-button"]'
  ];

  var BLOCK_NOTE_CLASS = "vx-checkout-block-note";
  var BLOCK_NOTE_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" ' +
    'fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" ' +
    'aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>' +
    '<line x1="12" y1="16" x2="12.01" y2="16"/></svg>';

  // Encuentra un contenedor ancho (bloque/columna completa) donde colocar el aviso,
  // evitando que caiga en el mismo flex-row del boton y quede comprimido al lado.
  function getNoteAnchor(btn) {
    var candidates = [
      ".cart-drawer__footer",
      ".drawer__footer",
      ".cart__footer",
      ".cart-drawer-footer",
      ".totals",
      'form[action*="/cart"]',
      "form"
    ];
    for (var i = 0; i < candidates.length; i++) {
      var anchor = btn.closest(candidates[i]);
      if (anchor) return anchor;
    }
    return btn.parentNode;
  }

  function findNoteFor(btn) {
    var anchor = getNoteAnchor(btn);
    if (!anchor) return null;
    return anchor.querySelector(":scope > ." + BLOCK_NOTE_CLASS);
  }

  function ensureBlockNote(btn, reason) {
    var existing = findNoteFor(btn);
    if (existing) {
      if (existing.getAttribute("data-reason") !== reason) {
        existing.setAttribute("data-reason", reason);
        existing.innerHTML = BLOCK_NOTE_ICON + '<span>' + escapeHtml(reason) + '</span>';
      }
      return;
    }
    var anchor = getNoteAnchor(btn);
    if (!anchor) return;
    var note = document.createElement("div");
    note.className = BLOCK_NOTE_CLASS;
    note.setAttribute("role", "alert");
    note.setAttribute("data-reason", reason);
    note.innerHTML = BLOCK_NOTE_ICON + '<span>' + escapeHtml(reason) + '</span>';
    // Insertamos al inicio del footer para que quede arriba del boton
    anchor.insertBefore(note, anchor.firstChild);
  }

  function removeBlockNote(btn) {
    var note = findNoteFor(btn);
    if (note && note.parentNode) note.parentNode.removeChild(note);
  }

  function updateCheckoutButton(msg) {
    var shouldBlock = !!(msg && msg.blockCheckout);
    var reason = (msg && msg.blockReason) || "Agrega mas productos para alcanzar el pedido minimo";

    var buttons = [];
    for (var i = 0; i < CHECKOUT_SELECTORS.length; i++) {
      var nodes = document.querySelectorAll(CHECKOUT_SELECTORS[i]);
      for (var j = 0; j < nodes.length; j++) buttons.push(nodes[j]);
    }
    if (!buttons.length) return;

    for (var k = 0; k < buttons.length; k++) {
      var btn = buttons[k];
      if (shouldBlock) {
        btn.setAttribute("disabled", "disabled");
        btn.setAttribute("aria-disabled", "true");
        btn.classList.add("vx-checkout-blocked");
        // Guardamos tooltip original una sola vez para poder restaurarlo
        if (!btn.hasAttribute("data-vx-orig-title")) {
          btn.setAttribute("data-vx-orig-title", btn.getAttribute("title") || "");
        }
        btn.setAttribute("title", reason);
        ensureBlockNote(btn, reason);
      } else {
        btn.removeAttribute("disabled");
        btn.removeAttribute("aria-disabled");
        btn.classList.remove("vx-checkout-blocked");
        if (btn.hasAttribute("data-vx-orig-title")) {
          var orig = btn.getAttribute("data-vx-orig-title");
          if (orig) btn.setAttribute("title", orig);
          else btn.removeAttribute("title");
          btn.removeAttribute("data-vx-orig-title");
        }
        removeBlockNote(btn);
      }
    }
  }

  var updating = false;

  function update() {
    if (updating) return;
    updating = true;
    var location = readLocation();
    fetchCart().then(function (cart) {
      var msg = buildMessage(location, cart);
      render(msg);
      updateCheckoutButton(msg);
      updating = false;
    });
  }

  var updateTimer = null;
  function scheduleUpdate() {
    if (updateTimer) clearTimeout(updateTimer);
    updateTimer = setTimeout(function () {
      updateTimer = null;
      update();
    }, 120);
  }

  // Observers internos (referenciados para poder desconectar al re-setup)
  var innerObservers = [];

  function disconnectInnerObservers() {
    for (var i = 0; i < innerObservers.length; i++) {
      try { innerObservers[i].disconnect(); } catch (e) {}
    }
    innerObservers = [];
  }

  function setupObservers() {
    disconnectInnerObservers();

    // 1) Cambios en items del carrito (subtotal cambia)
    var itemsContainer = document.getElementById("CartDrawer-CartItems");
    if (itemsContainer) {
      var o1 = new MutationObserver(scheduleUpdate);
      o1.observe(itemsContainer, {
        childList: true,
        subtree: true,
        characterData: true
      });
      innerObservers.push(o1);
    }

    // 2) Total del drawer (por si cambia sin tocar items)
    var totalEl = document.querySelector(".totals__total-value");
    if (totalEl) {
      var o2 = new MutationObserver(scheduleUpdate);
      o2.observe(totalEl, {
        childList: true,
        characterData: true,
        subtree: true
      });
      innerObservers.push(o2);
    }

    // 3) Apertura/cierre del drawer (class change)
    var drawer = document.querySelector("cart-drawer");
    if (drawer) {
      var o3 = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName === "class") {
            scheduleUpdate();
            return;
          }
        }
      });
      o3.observe(drawer, { attributes: true, attributeFilter: ["class"] });
      innerObservers.push(o3);
    }
  }

  // Vigila el body para detectar cuando el drawer/banner es reemplazado
  // (ej. addToCartSlideshow en featured-collection.liquid hace replaceWith,
  //  o Shopify re-renderiza el drawer con renderContents). En esos casos,
  //  el banner nace con placeholder "Calculando envío..." y hay que refrescar.
  function watchForBannerReplacement() {
    var lastBanner = document.getElementById(BANNER_ID);

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue; // solo elementos

          var isOrHasBanner =
            node.id === BANNER_ID ||
            (node.querySelector && node.querySelector("#" + BANNER_ID));

          if (isOrHasBanner) {
            var current = document.getElementById(BANNER_ID);
            if (current && current !== lastBanner) {
              lastBanner = current;
              setupObservers();   // re-anclar observers al DOM nuevo
              scheduleUpdate();   // repoblar el banner
            }
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    update();
    setupObservers();
    watchForBannerReplacement();

    // Evento existente emitido por location-system.js al guardar
    window.addEventListener("farmalkosto:location-changed", scheduleUpdate);

    // Eventos estandar que distintos temas pueden disparar
    document.addEventListener("cart:refresh", scheduleUpdate);
    document.addEventListener("cart:updated", scheduleUpdate);
    window.addEventListener("cart:updated", scheduleUpdate);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
